import type { AcquirerProfile } from "./types";

/**
 * Visa VAMP tier bands (Jan 2026 update):
 *   Acquirer-level
 *     - healthy:         < 0.500%
 *     - above_standard:  ≥ 0.500%   $8 per CNP transaction
 *     - excessive:       ≥ 0.700%   $8 per CNP transaction + enhanced monitoring
 *   Merchant-level
 *     - above_standard:  ≥ 0.500%   (Jan 2026+)
 *     - excessive:       ≥ 1.500%   (Apr 2026+, US/Canada/EU)
 */
export const ACQUIRER_VAMP_ABOVE_STANDARD = 0.5;
export const ACQUIRER_VAMP_EXCESSIVE = 0.7;
export const ACQUIRER_VAMP_FEE_PER_TXN_USD = 8;

export const MERCHANT_VAMP_ABOVE_STANDARD = 0.5;
export const MERCHANT_VAMP_EXCESSIVE = 1.5;

/** Kept for existing call sites referring to "the breach line" — merchant-level. */
export const MERCHANT_VAMP_CEILING = MERCHANT_VAMP_EXCESSIVE;
export const MERCHANT_VAMP_EARLY_WARNING = MERCHANT_VAMP_ABOVE_STANDARD;

export const ACQUIRER: AcquirerProfile = {
  name: "ACME Acquiring",
  processorRef: "ACME-VISA",
  visaMember: "Principal Member",
  region: "Europe",
  bins: [
    {
      bin: "486201",
      ccy: "EUR",
      label: "EUR BIN",
      binMonthlyVol: 18_400_000,
      binMonthlyTxns: 920_000,
      binVampRatio: 0.412,
      binFraudRatio: 0.331,
      binCbRatio: 0.081,
      vampAboveStandard: ACQUIRER_VAMP_ABOVE_STANDARD,
      vampExcessive: ACQUIRER_VAMP_EXCESSIVE,
      cbThreshold: 0.5,
    },
    {
      bin: "486202",
      ccy: "USD",
      label: "USD BIN",
      binMonthlyVol: 9_800_000,
      binMonthlyTxns: 510_000,
      binVampRatio: 0.438,
      binFraudRatio: 0.361,
      binCbRatio: 0.077,
      vampAboveStandard: ACQUIRER_VAMP_ABOVE_STANDARD,
      vampExcessive: ACQUIRER_VAMP_EXCESSIVE,
      cbThreshold: 0.5,
    },
    {
      bin: "486203",
      ccy: "GBP",
      label: "GBP BIN",
      binMonthlyVol: 4_100_000,
      binMonthlyTxns: 195_000,
      binVampRatio: 0.287,
      binFraudRatio: 0.221,
      binCbRatio: 0.066,
      vampAboveStandard: ACQUIRER_VAMP_ABOVE_STANDARD,
      vampExcessive: ACQUIRER_VAMP_EXCESSIVE,
      cbThreshold: 0.5,
    },
  ],
  otherSegments: [
    { name: "retail / e-commerce", mcc: "5411-5999", vol: 6_200_000, vampRatio: 0.21 },
    { name: "travel & transport", mcc: "4511-4722", vol: 4_100_000, vampRatio: 0.38 },
    { name: "hospitality", mcc: "7011-7999", vol: 3_300_000, vampRatio: 0.29 },
    { name: "digital services", mcc: "7372-7379", vol: 2_050_000, vampRatio: 0.19 },
    { name: "fuel & auto", mcc: "5541-5571", vol: 1_250_000, vampRatio: 0.09 },
  ],
  internalBufferBps: 5,
  vampAboveStandard: ACQUIRER_VAMP_ABOVE_STANDARD,
  vampExcessive: ACQUIRER_VAMP_EXCESSIVE,
  vampFeePerTxnUsd: ACQUIRER_VAMP_FEE_PER_TXN_USD,
  cbThreshold: 0.5,
};

export function weightedBinVamp(acquirer: AcquirerProfile): number {
  const totalVol = acquirer.bins.reduce((s, b) => s + b.binMonthlyVol, 0);
  if (totalVol === 0) return 0;
  const weighted = acquirer.bins.reduce(
    (s, b) => s + b.binVampRatio * b.binMonthlyVol,
    0
  );
  return Math.round((weighted / totalVol) * 1000) / 1000;
}

/** Classify a VAMP ratio against acquirer bands. */
export type VampBand = "healthy" | "above_standard" | "excessive";

export function acquirerBand(vampPct: number): VampBand {
  if (vampPct >= ACQUIRER_VAMP_EXCESSIVE) return "excessive";
  if (vampPct >= ACQUIRER_VAMP_ABOVE_STANDARD) return "above_standard";
  return "healthy";
}

export function merchantBand(vampPct: number): VampBand {
  if (vampPct >= MERCHANT_VAMP_EXCESSIVE) return "excessive";
  if (vampPct >= MERCHANT_VAMP_ABOVE_STANDARD) return "above_standard";
  return "healthy";
}
